import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { data, Link, redirect, useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	EventAnnouncementModal,
	type AnnouncedEvent,
} from '#app/components/event-announcement-modal.tsx'
import { RichContent } from '#app/components/rich-content.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { BeeldPickerProvider } from '#app/contexts/beeld-picker-context.tsx'
import { type Language } from '#app/i18n.server.ts'
import {
	EASE_OUT_QUINT,
	fadeInUp,
	staggerContainer,
	staggerItem,
} from '#app/utils/animations.ts'
import { getUserId, requireUserId } from '#app/utils/auth.server.ts'
import { HOME_SECTION_IDS } from '#app/utils/content-sections.ts'
import { prisma } from '#app/utils/db.server.ts'
import { formatEventDateRange } from '#app/utils/event-dates.ts'
import { cn, getNoteImgSrc } from '#app/utils/misc.tsx'
import {
	buildMeta,
	buildWebSiteJsonLd,
	buildOrganizationJsonLd,
	metaDescriptionFromContent,
} from '#app/utils/seo.ts'
import { titleToSlug } from '#app/utils/slug.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { type Route } from './+types/index.ts'

const AddFeaturedImageSchema = z.object({
	intent: z.literal('add-featured'),
	noteImageId: z.string(),
})

const RemoveFeaturedImageSchema = z.object({
	intent: z.literal('remove-featured'),
	featuredImageId: z.string(),
})

const ReorderFeaturedImageSchema = z.object({
	intent: z.literal('reorder-featured'),
	draggedImageId: z.string(),
	targetImageId: z.string(),
	newPosition: z.string().transform((val) => parseInt(val, 10)),
})

export const handle = { i18n: 'common' }

interface MediaAbout {
	overview: string
	conclusion: string
}

interface MediaContent {
	src: string
	poster?: string
	background: string
	title: string
	date: string
	scrollToExpand: string
	about: MediaAbout
}

const sampleMediaContent: MediaContent = {
	src: 'https://images.unsplash.com/photo-1682687982501-1e58ab814714?q=80&w=1280&auto=format&fit=crop',
	background:
		'https://images.unsplash.com/photo-1506744038136-46273834b3fb?q=80&w=1920&auto=format&fit=crop',
	title: 'Fragments of Homo Ludens',
	date: 'Home',
	scrollToExpand: 'Scroll to Expand',
	about: {
		overview:
			'In haar werk onderzoekt Else Ringnalda hoe een ideale samenleving eruit ziet: Een wereld waarin iedereen van waarde is en een waarde in zichzelf heeft. Zij stelt zich een samenleving voor waarin mensen elkaar respecteren en open staan voor elkaars verschillen ongeacht gender, religie, etniciteit of positie in de samenleving. Met haar figuratieve beelden wil zij deze visie tot uiting brengen en toeschouwers uitnodigen om te reflecteren op hun eigen waarde, hun relatie tot anderen en onze natuurlijke omgeving.',
		conclusion: 'Lees verder',
	},
}

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	const { getLocale } = await import('#app/utils/locale.server.ts')
	const language = (await getLocale(request)) as Language

	// "Current announced event": temporary exhibition running now or starting
	// within 30 days (homepage modal + CTA band)
	const now = new Date()
	const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)

	// Run independent queries in parallel
	const [featuredImagesRaw, homeContent, currentEventRaw, adminData] =
		await Promise.all([
		prisma.featuredImage.findMany({
			select: {
				noteImage: {
					select: {
						objectKey: true,
						altText: true,
						note: { select: { title: true } },
					},
				},
			},
			orderBy: { displayOrder: 'asc' },
		}),
		// Try the requested language first, then fall back to nl (the editorial
		// source) so a missing translation shows the latest CMS Dutch instead
		// of the stale string baked into sampleMediaContent.about.overview.
		prisma.contentSection.findMany({
			where: {
				sectionId: HOME_SECTION_IDS.OVERVIEW,
				language: { in: [language, 'nl'] },
			},
		}),
		// Event selection: an admin-pinned event wins as long as it's still due
		// or running (no 30-day window, no isCurrent/type requirement — the
		// drawer can pin any event with dates, and an ended pin is excluded by
		// the endDate filter). Otherwise fall back to the auto-picked one.
		// Both candidates are fetched in parallel; enrichment happens once below.
		Promise.all([
			prisma.exhibition.findFirst({
				where: {
					featuredOnHomepage: true,
					beginDate: { not: null },
					OR: [{ endDate: null }, { endDate: { gte: now } }],
				},
				orderBy: { beginDate: 'asc' },
			}),
			prisma.exhibition.findFirst({
				where: {
					type: 'exhibition',
					isCurrent: true,
					beginDate: { not: null, lte: in30Days },
					OR: [{ endDate: null }, { endDate: { gte: now } }],
				},
				orderBy: { beginDate: 'asc' },
			}),
		]).then(([pinned, auto]) => pinned ?? auto),
		userId
			? Promise.all([
					prisma.noteImage.findMany({
						select: {
							id: true,
							objectKey: true,
							altText: true,
							note: { select: { title: true } },
						},
						orderBy: { createdAt: 'desc' },
					}),
					prisma.featuredImage.findMany({
						select: {
							id: true,
							displayOrder: true,
							noteImage: {
								select: {
									id: true,
									objectKey: true,
									altText: true,
									note: { select: { title: true } },
								},
							},
						},
						orderBy: { displayOrder: 'asc' },
					}),
				])
			: Promise.resolve([[] as any[], [] as any[]]),
	])

	const [availableImages, allFeaturedImages] = adminData

	let heroImages: Array<{ src: string; title: string; alt: string | null }>
	if (featuredImagesRaw.length > 0) {
		const seen = new Set<string>()
		heroImages = []
		for (const f of featuredImagesRaw) {
			if (seen.has(f.noteImage.note.title)) continue
			seen.add(f.noteImage.note.title)
			heroImages.push({
				src: getNoteImgSrc(f.noteImage.objectKey),
				title: f.noteImage.note.title,
				alt: f.noteImage.altText,
			})
		}
	} else {
		const notes = await prisma.note.findMany({
			select: {
				title: true,
				images: {
					select: { objectKey: true, altText: true },
					take: 1,
				},
			},
			where: { images: { some: {} } },
			orderBy: { updatedAt: 'desc' },
			take: 6,
		})
		heroImages = notes
			.filter((n) => n.images[0])
			.map((n) => ({
				src: getNoteImgSrc(n.images[0]!.objectKey),
				title: n.title,
				alt: n.images[0]!.altText,
			}))
	}

	// Resolve translation + image for the announced event, if any
	let currentEvent: AnnouncedEvent | null = null
	if (currentEventRaw?.beginDate) {
		const [translation, noteImage] = await Promise.all([
			prisma.exhibitionTranslation.findUnique({
				where: {
					exhibitionId_language: {
						exhibitionId: currentEventRaw.id,
						language,
					},
				},
			}),
			!currentEventRaw.imageObjectKey && currentEventRaw.imageId
				? prisma.noteImage.findUnique({
						where: { id: currentEventRaw.imageId },
						select: { objectKey: true, altText: true },
					})
				: Promise.resolve(null),
		])

		const imageObjectKey =
			currentEventRaw.imageObjectKey ?? noteImage?.objectKey ?? null

		currentEvent = {
			id: currentEventRaw.id,
			title: translation?.title || currentEventRaw.title,
			description: translation?.description || currentEventRaw.description,
			beginDate: currentEventRaw.beginDate.toISOString(),
			endDate: currentEventRaw.endDate?.toISOString() ?? null,
			address: currentEventRaw.address,
			websiteUrl: currentEventRaw.websiteUrl,
			googleMapsUrl: currentEventRaw.googleMapsUrl,
			image: imageObjectKey
				? {
						src: getNoteImgSrc(imageObjectKey),
						alt: currentEventRaw.imageAltText ?? noteImage?.altText ?? null,
					}
				: null,
			isRunning: currentEventRaw.beginDate <= now,
		}
	}

	return data({
		heroImages,
		userId,
		availableImages,
		featuredImages: allFeaturedImages,
		locale: language,
		currentEvent,
		overviewContent:
			homeContent.find((c) => c.language === language)?.content ||
			homeContent.find((c) => c.language === 'nl')?.content ||
			sampleMediaContent.about.overview,
	})
}

export async function action({ request }: Route.ActionArgs) {
	await requireUserId(request)

	const formData = await request.formData()
	const intent = formData.get('intent')

	if (intent === 'add-featured') {
		const submission = parseWithZod(formData, {
			schema: AddFeaturedImageSchema,
		})

		if (submission.status !== 'success') {
			return data(
				{ result: submission.reply() },
				{ status: submission.status === 'error' ? 400 : 200 },
			)
		}

		const { noteImageId } = submission.value

		// Check if image exists
		const noteImage = await prisma.noteImage.findFirst({
			where: { id: noteImageId },
		})
		invariantResponse(noteImage, 'Afbeelding niet gevonden', { status: 404 })

		// Check if already featured
		const existingFeatured = await prisma.featuredImage.findFirst({
			where: { noteImageId },
		})

		if (existingFeatured) {
			return data(
				{
					result: {
						status: 'error',
						error: 'Deze afbeelding is al geselecteerd',
					},
				},
				{ status: 400 },
			)
		}

		// Get next display order
		const maxOrder = await prisma.featuredImage.findFirst({
			select: { displayOrder: true },
			orderBy: { displayOrder: 'desc' },
		})

		const nextOrder = (maxOrder?.displayOrder ?? 0) + 1

		await prisma.featuredImage.create({
			data: {
				noteImageId,
				displayOrder: nextOrder,
			},
		})

		return redirectWithToast('/', {
			type: 'success',
			title: 'Succes',
			description: 'Afbeelding toegevoegd aan homepage',
		})
	}

	if (intent === 'remove-featured') {
		const submission = parseWithZod(formData, {
			schema: RemoveFeaturedImageSchema,
		})

		if (submission.status !== 'success') {
			return data(
				{ result: submission.reply() },
				{ status: submission.status === 'error' ? 400 : 200 },
			)
		}

		const { featuredImageId } = submission.value

		const featuredImage = await prisma.featuredImage.findFirst({
			where: { id: featuredImageId },
		})
		invariantResponse(featuredImage, 'Geselecteerde afbeelding niet gevonden', {
			status: 404,
		})

		await prisma.featuredImage.delete({
			where: { id: featuredImageId },
		})

		return redirectWithToast('/', {
			type: 'success',
			title: 'Succes',
			description: 'Afbeelding verwijderd van homepage',
		})
	}

	if (intent === 'reorder-featured') {
		const submission = parseWithZod(formData, {
			schema: ReorderFeaturedImageSchema,
		})

		if (submission.status !== 'success') {
			return data(
				{ result: submission.reply() },
				{ status: submission.status === 'error' ? 400 : 200 },
			)
		}

		const { draggedImageId, targetImageId, newPosition } = submission.value

		// Get all featured images in order
		const allFeaturedImages = await prisma.featuredImage.findMany({
			orderBy: { displayOrder: 'asc' },
		})

		// Find the dragged and target items
		const draggedItem = allFeaturedImages.find(
			(img) => img.id === draggedImageId,
		)
		const targetItem = allFeaturedImages.find((img) => img.id === targetImageId)

		if (!draggedItem || !targetItem) {
			return data(
				{ result: { status: 'error', error: 'Afbeelding niet gevonden' } },
				{ status: 404 },
			)
		}

		// Remove the dragged item from its current position
		const filteredImages = allFeaturedImages.filter(
			(img) => img.id !== draggedImageId,
		)

		// Insert the dragged item at the new position
		filteredImages.splice(newPosition, 0, draggedItem)

		// Update display orders for all affected items
		await Promise.all(
			filteredImages.map((img, index) =>
				prisma.featuredImage.update({
					where: { id: img.id },
					data: { displayOrder: index },
				}),
			),
		)

		return redirectWithToast('/', {
			type: 'success',
			title: 'Succes',
			description: 'Volgorde bijgewerkt',
		})
	}

	return redirect('/')
}

interface HeroImage {
	src: string
	title: string
	alt: string | null
}

function useCyclingPanels(allImages: string[], initialImages: string[]) {
	const [panels, setPanels] = useState(initialImages)
	const nextPanelRef = useRef(0)
	const usedImagesRef = useRef(new Set(initialImages))

	// One timer subscription per image set. All panel-state reads happen inside
	// the functional updater so `panels` never needs to be a dependency (a
	// `panels` dep re-subscribed on every swap and leaked the old interval,
	// which made the cross-fade accelerate over time).
	const imagesKey = allImages.join('|')
	useEffect(() => {
		const images = imagesKey === '' ? [] : imagesKey.split('|')
		if (images.length <= 3) return

		let timer: ReturnType<typeof setTimeout> | undefined

		const swap = () => {
			const panelIndex = nextPanelRef.current
			setPanels((prev) => {
				let available = images.filter((img) => !usedImagesRef.current.has(img))
				if (available.length === 0) {
					usedImagesRef.current = new Set(prev)
					available = images.filter((img) => !usedImagesRef.current.has(img))
				}
				const newImg =
					available[Math.floor(Math.random() * available.length)] ?? images[0]
				if (!newImg || newImg === prev[panelIndex]) return prev

				const next = [...prev]
				usedImagesRef.current.delete(next[panelIndex]!)
				next[panelIndex] = newImg
				usedImagesRef.current.add(newImg)
				return next
			})
			nextPanelRef.current = (panelIndex + 1) % 3
		}

		// self-rescheduling chain: exactly one pending timer at any moment,
		// with a genuinely random 6-8s cadence per tick
		const tick = () => {
			swap()
			timer = setTimeout(tick, 6000 + Math.random() * 2000)
		}
		timer = setTimeout(tick, 8000)

		return () => clearTimeout(timer)
	}, [imagesKey])

	return panels
}

function TriptychPanel({
	src,
	index,
	shouldReduceMotion,
}: {
	src: string
	index: number
	shouldReduceMotion: boolean | null
}) {
	return (
		// The 1px paper separator between panels is desktop-only: at mobile
		// width it reads as a stray white line through the images.
		<div
			className={cn(
				'relative h-full overflow-hidden',
				index < 2 && 'md:border-r-paper md:border-r',
			)}
		>
			<AnimatePresence mode="popLayout">
				<motion.img
					key={src}
					src={`${src}${src.includes('?') ? '&' : '?'}w=1280&format=webp`}
					alt=""
					className="absolute inset-0 h-full w-full object-cover"
					style={{
						filter: 'saturate(0.9) brightness(0.85)',
						willChange: 'transform, opacity',
					}}
					initial={shouldReduceMotion ? false : { opacity: 0, scale: 1.06 }}
					animate={{
						opacity: 1,
						scale: [1, 1.04, 1],
					}}
					exit={{ opacity: 0 }}
					transition={{
						opacity: { duration: 1.4, ease: EASE_OUT_QUINT },
						scale: { duration: 12, ease: 'easeInOut', repeat: Infinity, delay: index * 1.5 },
					}}
				/>
			</AnimatePresence>
			<motion.div
				className="absolute bottom-6 left-6 z-10 uppercase"
				style={{ color: 'var(--paper)', fontSize: 11, letterSpacing: '0.18em' }}
				initial={shouldReduceMotion ? { opacity: 0.7 } : { opacity: 0, y: 28 }}
				animate={{ opacity: 0.7, y: 0 }}
				transition={{ duration: 1.2, ease: EASE_OUT_QUINT, delay: index * 0.22 + 0.8 }}
			>
				{['I', 'II', 'III'][index]}
			</motion.div>
		</div>
	)
}

function TriptychHero({ heroImages }: { heroImages: HeroImage[] }) {
	const fallback = sampleMediaContent.src
	const allSrcs = heroImages.map((h) => h.src)
	const initial = [
		allSrcs[1] || allSrcs[0] || fallback,
		allSrcs[0] || fallback,
		allSrcs[2] || allSrcs[0] || fallback,
	]
	const panels = useCyclingPanels(allSrcs, initial)
	const shouldReduceMotion = useReducedMotion()

	return (
		<section className="relative h-screen overflow-hidden" style={{ background: 'var(--ink)' }}>
			<div className="absolute inset-0 grid grid-cols-3">
				{panels.map((src, i) => (
					<TriptychPanel
						key={i}
						src={src}
						index={i}
						shouldReduceMotion={shouldReduceMotion}
					/>
				))}
			</div>

			<motion.div
				className="pointer-events-none absolute inset-0 flex items-center justify-center p-6 text-center"
				initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.96 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 1.8, ease: EASE_OUT_QUINT, delay: 1.8 }}
			>
				<div style={{ color: 'var(--paper)', mixBlendMode: 'difference' }}>
					<div className="eyebrow mb-5" style={{ color: 'inherit', opacity: 0.85 }}>
						Solo · 2026
					</div>
					<h1
						className="display"
						style={{ fontSize: 'clamp(56px, 10vw, 144px)', letterSpacing: '-0.03em', lineHeight: 0.95 }}
					>
						Fragments of<br />Homo Ludens
					</h1>
				</div>
			</motion.div>
		</section>
	)
}

function FeaturedMosaic({ heroImages }: { heroImages: HeroImage[] }) {
	const { t } = useTranslation()
	const gridConfigs = [
		{ col: 'span 5', row: 'span 4' },
		{ col: 'span 4', row: 'span 3' },
		{ col: 'span 3', row: 'span 5' },
		{ col: 'span 4', row: 'span 4' },
		{ col: 'span 4', row: 'span 3' },
		{ col: 'span 4', row: 'span 4' },
	]

	return (
		<motion.section
			className="py-20"
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, amount: 0.15 }}
			variants={fadeInUp}
		>
			<div className="container">
				<div className="mb-12 flex items-baseline justify-between">
					<h2 className="display" style={{ fontSize: 'clamp(32px, 5vw, 64px)' }}>
						{t('homeSelectedWork')}
					</h2>
					<Link to="/beelden" className="btn-gallery-link">
						{t('homeViewAll')} <Icon name="arrow-right" className="h-4 w-4" />
					</Link>
				</div>
				<motion.div
					className="grid gap-4"
					style={{
						gridTemplateColumns: 'repeat(12, 1fr)',
						gridAutoRows: '120px',
					}}
					initial="initial"
					whileInView="animate"
					viewport={{ once: true, amount: 0.1 }}
					variants={staggerContainer}
				>
					{gridConfigs.map((cfg, i) => {
						const image = heroImages[i]
						if (!image) return null
						return (
							<motion.article
								key={i}
								variants={staggerItem}
								className="group relative overflow-hidden"
								style={{
									gridColumn: cfg.col,
									gridRow: cfg.row,
									background: 'var(--paper-2)',
								}}
							>
								<Link
									to={`/beelden/${titleToSlug(image.title)}`}
									className="block h-full w-full"
									viewTransition
									discover="none"
								>
									<Img
										src={image.src}
										alt={image.alt || image.title}
										width={800}
										height={600}
										fit="cover"
										className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
									/>
									<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
									<div className="absolute right-0 bottom-0 left-0 translate-y-4 p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
										<h3
											className="font-heading text-lg font-medium"
											style={{ color: '#fff' }}
										>
											{image.title}
										</h3>
									</div>
								</Link>
							</motion.article>
						)
					})}
				</motion.div>
			</div>
		</motion.section>
	)
}

function ExhibitionCTA({
	event,
	fallbackImageSrc,
}: {
	event: AnnouncedEvent | null
	fallbackImageSrc?: string
}) {
	const { t, i18n } = useTranslation()
	if (!event) return null

	const dateRange = formatEventDateRange(
		event.beginDate,
		event.endDate,
		i18n.language,
		{ from: t('from'), until: t('until') },
	)
	const subline = [dateRange, event.address].filter(Boolean).join(' · ')

	return (
		<motion.section
			className="py-20"
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, amount: 0.15 }}
			variants={fadeInUp}
		>
			<div className="container">
				<div
					className="grid items-center gap-12 md:grid-cols-2"
					style={{
						background: 'var(--ink)',
						color: 'var(--paper)',
						padding: 'clamp(40px, 6vw, 80px)',
					}}
				>
					<div>
						<div className="eyebrow mb-4" style={{ color: 'var(--muted-2)' }}>
							{event.isRunning ? t('homeNowShowing') : t('eventUpcoming')}
						</div>
						<h2 className="display mb-6" style={{ fontSize: 'clamp(32px, 4vw, 56px)' }}>
							{event.title}
						</h2>
						<p className="mb-8 text-[15px] opacity-80" style={{ maxWidth: '40ch' }}>
							{subline}
						</p>
						<Link
							to="/exposities"
							className="btn-gallery inline-flex"
							style={{
								background: 'var(--paper)',
								color: 'var(--ink)',
								borderColor: 'var(--paper)',
							}}
						>
							{t('homePlanVisit')} <Icon name="arrow-right" className="h-4 w-4" />
						</Link>
					</div>
					<Img
						src={event.image?.src ?? fallbackImageSrc ?? sampleMediaContent.src}
						alt={event.image?.alt ?? ''}
						width={800}
						height={1000}
						fit="cover"
						className="w-full object-cover"
						style={{ aspectRatio: '4/5' }}
					/>
				</div>
			</div>
		</motion.section>
	)
}

function NewsletterSection() {
	const { t } = useTranslation()
	const fetcher = useFetcher<{ success?: boolean }>()
	const isSubmitting = fetcher.state !== 'idle'
	const isSubscribed = Boolean(fetcher.data?.success)

	return (
		<motion.section
			className="py-[120px]"
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, amount: 0.15 }}
			variants={fadeInUp}
		>
			<div className="container">
				<div className="mx-auto max-w-[760px] text-center">
					<div className="eyebrow mb-6">{t('newsletterEyebrow')}</div>
					<h2
						className="display mb-6"
						style={{ fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
					>
						{t('newsletterTitle')}
					</h2>
					<p className="mx-auto mb-10 text-base" style={{ color: 'var(--ink-soft)', maxWidth: '52ch' }}>
						{t('newsletterText')}
					</p>
					{isSubscribed ? (
						<p className="mx-auto text-base font-medium" style={{ color: 'var(--ink)' }}>
							✓ {t('newsletterSubscribedTitle')} — {t('newsletterSubscribedText')}
						</p>
					) : (
						<fetcher.Form
							method="POST"
							action="/resources/newsletter-subscribe"
							className="mx-auto flex max-w-[480px] gap-2"
						>
							<HoneypotInputs />
								<input type="hidden" name="source" value="homepage" />
							<input
								type="email"
								name="email"
								required
								autoComplete="email"
								disabled={isSubmitting}
								placeholder={t('footerEmailPlaceholder')}
								className="flex-1 rounded-full border px-6 py-4 text-[15px] outline-none"
								style={{
									borderColor: 'var(--line)',
									background: 'var(--paper)',
									fontFamily: 'var(--font-sans)',
								}}
							/>
							<button
								type="submit"
								className="btn-gallery"
								disabled={isSubmitting}
								style={{ opacity: isSubmitting ? 0.6 : 1 }}
							>
								{isSubmitting ? '...' : t('newsletterSubscribe')}
							</button>
						</fetcher.Form>
					)}
				</div>
			</div>
		</motion.section>
	)
}

export default function Index({ loaderData }: Route.ComponentProps) {
	const { heroImages, overviewContent, currentEvent } = loaderData
	const { t } = useTranslation()

	return (
		<BeeldPickerProvider>
			<div>
				{/* Current-event announcement modal (opens shortly after mount) */}
				{currentEvent ? <EventAnnouncementModal event={currentEvent} /> : null}

				{/* Triptych Hero */}
				<TriptychHero heroImages={heroImages} />

				{/* Intro Section */}
				<motion.section
					className="pt-[120px] pb-20"
					initial="initial"
					whileInView="animate"
					viewport={{ once: true, amount: 0.15 }}
					variants={fadeInUp}
				>
					<div className="mx-auto max-w-[880px] px-6">
						<div className="eyebrow mb-6">{t('homeArtistSince')}</div>
						<div className="lede mb-12" style={{ maxWidth: '60ch' }}>
							<RichContent html={overviewContent} />
						</div>
						<div className="flex flex-wrap gap-4">
							<Link to="/beelden" className="btn-gallery" viewTransition>
								{t('homeViewWorks')} <Icon name="arrow-right" className="h-4 w-4" />
							</Link>
							<Link to="/exposities" className="btn-gallery btn-gallery-ghost" viewTransition>
								{t('currentExhibitions')}
							</Link>
						</div>
					</div>
				</motion.section>

				{/* Featured Works Mosaic */}
				<FeaturedMosaic heroImages={heroImages} />

				{/* Exhibition CTA Band */}
				<ExhibitionCTA
					event={currentEvent}
					fallbackImageSrc={heroImages[0]?.src}
				/>

				{/* Newsletter */}
				<NewsletterSection />
			</div>
		</BeeldPickerProvider>
	)
}

export const meta: Route.MetaFunction = ({ data }) => {
	// Prefer the CMS intro (in the request's language) over the static string.
	const description =
		(data?.overviewContent && metaDescriptionFromContent(data.overviewContent)) ||
		'Else Ringnalda, beeldend kunstenaar. Figuratieve sculpturen in brons en steen. Beelden in publieke ruimte en collecties door heel Europa.'

	return [
		...buildMeta({
			title: 'Else Ringnalda — Beeldend Kunstenaar',
			description,
			url: '/',
			locale: data?.locale,
		}),
		buildWebSiteJsonLd(),
		buildOrganizationJsonLd(),
	]
}
